import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import axiosInstance from '../../../api/axiosInstance';
import './RegisterScreen.css';

type Step = 'form' | 'code';

const RegisterScreen: React.FC = () => {
  const [step, setStep] = useState<Step>('form');
  const [inviteCode, setInviteCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [consentPersonalData, setConsentPersonalData] = useState(false);
  const [consentBeta, setConsentBeta] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const { loginWithTokens } = useAuth();
  const navigate = useNavigate();

  const sendCode = async () => {
    await axiosInstance.post('/auth/register-email', {
      firstName,
      lastName,
      email,
      password,
      inviteCode,
      consentPersonalData,
      consentBeta,
    });
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== password2) {
      setError('Пароли не совпадают');
      return;
    }
    if (password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }
    if (!consentPersonalData || !consentBeta) {
      setError('Необходимо принять оба согласия ниже');
      return;
    }

    setIsLoading(true);
    try {
      await sendCode();
      setStep('code');
      setVerifyCode('');
      setTimeout(() => codeInputRef.current?.focus(), 100);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Произошла ошибка. Попробуйте ещё раз');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResendInfo(null);

    setIsLoading(true);
    try {
      const res = await axiosInstance.post<{
        success: boolean;
        accessToken: string;
        refreshToken: string;
        user: { id: number; name: string };
      }>('/auth/verify-email-code', { email, code: verifyCode });

      loginWithTokens(res.data.accessToken, res.data.refreshToken, res.data.user);
      navigate('/dashboard-new', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Неверный код');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setResendInfo(null);
    setIsLoading(true);
    try {
      await sendCode();
      setResendInfo('Новый код отправлен');
      setVerifyCode('');
      codeInputRef.current?.focus();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Не удалось отправить код');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-screen">
      <div className="register-container">
        <div className="register-header">
          <h1 className="register-title">
            {step === 'form' ? 'Регистрация' : 'Подтверждение почты'}
          </h1>
          <p className="register-subtitle">Ryaskov.pro — Система управления ремонтом</p>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleSubmitForm} className="register-form">
            {error && (
              <div className="error-message">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="inviteCode" className="form-label">Код приглашения</label>
              <input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={e => setInviteCode(e.target.value)}
                placeholder="Код для бета-тестирования"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="off"
              />
              <small className="form-help">
                Регистрация доступна по приглашению. Код можно получить у администратора
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="firstName" className="form-label">Имя</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Иван"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="given-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">Фамилия</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Петров"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="family-name"
              />
              <small className="form-help">
                В списках будет отображаться как «Имя Ф.» — фамилия видна не полностью
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Пароль</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Минимум 6 символов"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password2" className="form-label">Повторите пароль</label>
              <input
                id="password2"
                type="password"
                value={password2}
                onChange={e => setPassword2(e.target.value)}
                placeholder="Повторите пароль"
                className="form-input"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div className="consent-group">
              <label className="consent-row">
                <input
                  type="checkbox"
                  checked={consentPersonalData}
                  onChange={e => setConsentPersonalData(e.target.checked)}
                  disabled={isLoading}
                />
                <span>
                  Я даю согласие на{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    обработку персональных данных
                  </a>
                </span>
              </label>

              <label className="consent-row">
                <input
                  type="checkbox"
                  checked={consentBeta}
                  onChange={e => setConsentBeta(e.target.checked)}
                  disabled={isLoading}
                />
                <span>
                  Я ознакомлен(а) и согласен(на) с{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    условиями использования бета-версии
                  </a>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="register-button"
              disabled={
                isLoading || !inviteCode || !firstName || !lastName || !email ||
                !password || !password2 || !consentPersonalData || !consentBeta
              }
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line ri-spin" />
                  Отправляем код…
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line" />
                  Получить код
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitCode} className="register-form">
            <p className="code-sent-text">
              Мы отправили 6-значный код на{' '}
              <span className="success-email">{email}</span>
            </p>

            {error && (
              <div className="error-message">
                <i className="ri-error-warning-line" />
                {error}
              </div>
            )}
            {resendInfo && (
              <div className="info-message">
                <i className="ri-mail-check-line" />
                {resendInfo}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="verifyCode" className="form-label">Код из письма</label>
              <input
                id="verifyCode"
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="form-input code-input"
                required
                disabled={isLoading}
                autoComplete="one-time-code"
              />
              <small className="form-help">
                Код действителен 15 минут. Не пришло? Проверьте «Спам»
              </small>
            </div>

            <button
              type="submit"
              className="register-button"
              disabled={isLoading || verifyCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line ri-spin" />
                  Проверяем…
                </>
              ) : (
                <>
                  <i className="ri-checkbox-circle-line" />
                  Подтвердить и войти
                </>
              )}
            </button>

            <button
              type="button"
              className="register-back-button"
              onClick={handleResend}
              disabled={isLoading}
            >
              <i className="ri-refresh-line" />
              Отправить код ещё раз
            </button>

            <button
              type="button"
              className="register-back-button"
              onClick={() => { setStep('form'); setError(null); setResendInfo(null); }}
              disabled={isLoading}
            >
              <i className="ri-arrow-left-line" />
              Изменить данные
            </button>
          </form>
        )}

        {step === 'form' && (
          <div className="register-footer">
            <p className="login-link-text">
              Уже есть аккаунт?{' '}
              <button className="login-link" onClick={() => navigate('/login')}>
                Войти
              </button>
            </p>
            <button
              onClick={() => navigate('/')}
              className="register-back-button"
              disabled={isLoading}
            >
              <i className="ri-arrow-left-line" />
              На главную
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterScreen;
